import React from 'react';
import {
    TextField,
    InputAdornment,
    Box,
    FormHelperText
} from '@mui/material';
import { styled } from '@mui/material/styles';

const StyledInput = styled(TextField)(({ theme }) => ({
    '& .MuiInputLabel-root': {
        position: 'absolute',
        transform: 'translate(14px, 9px) scale(1)',
        fontSize: '0.875rem',
        color: theme.palette.text.secondary,
        fontWeight: 500,
        pointerEvents: 'none',
        backgroundColor: 'transparent',

        '&.Mui-focused': {
            color: theme.palette.primary.main,
        },

        '&.Mui-error': {
            color: theme.palette.error.main,
        },

        '&.MuiInputLabel-shrink': {
            transform: 'translate(14px, -9px) scale(0.75)',
            backgroundColor: theme.palette.background.paper,
            padding: '0 4px',
            marginLeft: '-4px',
        },
    },

    '& .MuiOutlinedInput-root': {
        backgroundColor: theme.palette.background.paper,
        fontSize: '0.875rem',

        '& input': {
            padding: '8.5px 14px',
            height: 'auto',

            '&::placeholder': {
                color: theme.palette.text.disabled,
                opacity: 0,
            },
        },

        '& textarea': {
            padding: '8.5px 14px',
        },

        '& fieldset': {
            borderColor: theme.palette.divider,
            transition: 'all 0.2s ease-in-out',
        },

        '&:hover fieldset': {
            borderColor: theme.palette.text.disabled,
        },

        '&.Mui-focused fieldset': {
            borderColor: theme.palette.primary.main,
            borderWidth: '1.5px',
        },

        '&.Mui-error fieldset': {
            borderColor: theme.palette.error.main,
        },

        '&.Mui-disabled': {
            backgroundColor: theme.palette.action.hover,
            '& fieldset': {
                borderColor: theme.palette.divider,
            },
        },

        '& input:-webkit-autofill': {
            WebkitBoxShadow: `0 0 0 1000px ${theme.palette.background.paper} inset`,
            WebkitTextFillColor: theme.palette.text.primary,
        },
    },

    '& .MuiOutlinedInput-notchedOutline': {
        '& legend': {
            maxWidth: '100%',
            transition: 'maxWidth 100ms cubic-bezier(0.0, 0, 0.2, 1) 50ms',
        },
    },

    '& .MuiInputLabel-shrink ~ .MuiOutlinedInput-root input::placeholder': {
        opacity: 1,
    },

    // Filled variant
    '& .MuiFilledInput-root': {
        backgroundColor: theme.palette.action.hover,
        fontSize: '0.875rem',

        '&:before, &:after': {
            borderBottomColor: theme.palette.divider,
        },

        '&:hover:before': {
            borderBottomColor: theme.palette.text.disabled,
        },

        '&.Mui-focused:after': {
            borderBottomColor: theme.palette.primary.main,
        },

        '& input': {
            padding: '25px 12px 8px',
        },
    },

    // Standard variant
    '& .MuiInput-root': {
        fontSize: '0.875rem',

        '&:before': {
            borderBottomColor: theme.palette.divider,
        },

        '&:hover:not(.Mui-disabled):before': {
            borderBottomColor: theme.palette.text.disabled,
        },

        '&.Mui-focused:after': {
            borderBottomColor: theme.palette.primary.main,
        },

        '& input': {
            padding: '4px 0 5px',
        },
    },
}));

const LsTextField = ({
                         label,
                         required = false,
                         error = false,
                         helperText,
                         placeholder,
                         disabled = false,
                         multiline = false,
                         rows = 4,
                         type = 'text',
                         startAdornment,
                         endAdornment,
                         fullWidth = true,
                         value,
                         onChange,
                         variant = 'outlined',
                         size = 'small',
                         InputLabelProps,
                         ...props
                     }) => {
    const inputProps = {};

    if (startAdornment) {
        inputProps.startAdornment = (
            <InputAdornment position="start">
                {startAdornment}
            </InputAdornment>
        );
    }

    if (endAdornment) {
        inputProps.endAdornment = (
            <InputAdornment position="end">
                {endAdornment}
            </InputAdornment>
        );
    }

    return (
        <Box sx={{ width: fullWidth ? '100%' : 'auto' }}>
            <StyledInput
                label={label}
                required={required}
                placeholder={placeholder}
                error={error}
                disabled={disabled}
                multiline={multiline}
                rows={multiline ? rows : undefined}
                type={type}
                fullWidth={fullWidth}
                value={value}
                onChange={onChange}
                variant={variant}
                size={size}
                InputLabelProps={{
                    shrink: undefined,
                    ...InputLabelProps,
                }}
                InputProps={Object.keys(inputProps).length > 0 ? inputProps : undefined}
                {...props}
            />

            {helperText && (
                <FormHelperText
                    error={error}
                    sx={{
                        ml: variant === 'outlined' ? '14px' : 0,
                        mt: '4px',
                        fontSize: '0.75rem',
                        lineHeight: '1.4',
                    }}
                >
                    {helperText}
                </FormHelperText>
            )}
        </Box>
    );
};

export default LsTextField;